import React, {
  useState, useEffect, useImperativeHandle, forwardRef
} from 'react';
import { PropTypes } from 'prop-types';
import {
  Container, Grid, Button, Card, CardContent, Typography, IconButton, Alert, Link
} from '@mui/material';
import {
  useFormContext, useFieldArray
} from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FormAutocomplete from '@seccon/core/components/FormAutocomplete';
import FormTextField from '@seccon/core/components/FormTextField';
import FormDatePicker from '@seccon/core/components/FormDatePicker';
import FormDropDown from '@seccon/core/components/FormDropDown';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { fetchCountryOptions } from '@seccon/shared/utils/destinationUtils';

import usePreviousDestinations from '../hooks/usePreviousDestinations';

function getDestinationName (destination) {
  const { city, country } = destination;
  return `${ city }${ city !== '' && country !== '' ? ', ' : '' }${ country }`;
}

const DestinationsForm = forwardRef(({ initialData }, ref) => {
  const {
    control, trigger, formState: { errors }, getValues, watch
  } = useFormContext();
  const {
    fields, append, remove, move
  } = useFieldArray({
    control,
    name: 'foreignTravel.destinations'
  });
  const destinations = watch().foreignTravel.destinations;

  useEffect(() => {
    if (fields.length === 0) {
      append({
        city: '', country: '', startDate: null, endDate: null, transportationMethodTo: '', transportationToInformation: '', transportationMethodWithin: '', transportationWithinInformation: '', stayInformation: ''
      });
    }
  }, [append, fields.length]);

  const [warnings, setWarnings] = useState([]);

  const formData = watch();
  const { data: previousDestinations } = usePreviousDestinations(formData.foreignTravel.travelingPersonnel.id.id);
  const [prevDestinationsDetails, setPreviousDestinationsDetails] = useState(null);

  useEffect(() => {
    if (previousDestinations && previousDestinations.length > 0) {
      // Fetch previous destination details for each destination
      const fetchDestinationDetails = async () => {
        const details = await Promise.all(
          previousDestinations.map(async (destination) => {
            const response = await fetch(`/foreignTravel/personnel/${ formData.foreignTravel.travelingPersonnel.id.id }/destination?city=${ destination.city }&country=${ destination.country }`);
            const detail = await response.json();

            // Handle instances if the same destination with the same start/end date is reported multiple times
            const dedupedDetails = detail?.destinations?.reduce((list, entry) => {
              const {
                city,
                country,
                startDate,
                endDate
              } = entry;
              const key = [city, country, startDate, endDate].filter(Boolean).join('-');

              // eslint-disable-next-line no-param-reassign
              list[key] = entry;

              return list;
            }, {});

            detail.destinations = Object.values(dedupedDetails);

            return detail;
          })
        );
        setPreviousDestinationsDetails(details);
      };

      fetchDestinationDetails();
    }
  }, [previousDestinations]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTrips, setSelectedTrips] = useState({});
  const [openDestination, setOpenDestination] = useState({});
  const [checkedDestinations, setCheckedDestinations] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);

  const handleClickOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedTrips({});
    setOpenDestination({});
    setCheckedDestinations({});
  };

  const handleAddPreviousDestinations = () => {
    if (prevDestinationsDetails && prevDestinationsDetails.length > 0) {
      for (const detail of prevDestinationsDetails) {
        for (const destination of detail.destinations) {
          const destinationKey = `${ destination.city }${ destination.country }`;
          const destinationValue = `${ destination.startDate }-${ destination.endDate }`;

          if (checkedDestinations[destinationKey] && selectedTrips[destinationKey] === destinationValue) {
            append({
              city: destination.city,
              country: destination.country,
              startDate: null,
              endDate: null,
              transportationMethodTo: destination.transportationMethodTo,
              transportationToInformation: destination.transportationToInformation,
              transportationMethodWithin: destination.transportationMethodWithin,
              transportationWithinInformation: destination.transportationWithinInformation,
              stayInformation: destination.stayInformation
            });
          }
        }
      }
    }
    setModalOpen(false);
    setSelectedTrips({});
    setOpenDestination({});
    setCheckedDestinations({});
  }

  const handleToggleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = () => {
    handleCloseMenu();
    handleClickOpen();
  };

  const handleTripSelect = (destinationName, trip) => {
    setSelectedTrips((prev) => ({
      ...prev,
      [destinationName]: trip
    }));
  };

  const handleDestinationClick = (destinationName) => {
    setOpenDestination((prev) => ({
      ...prev,
      [destinationName]: !prev[destinationName]
    }));
  };

  const handleDestinationCheck = (destinationName, index) => {
    setCheckedDestinations((prev) => ({
      ...prev,
      [destinationName]: !prev[destinationName]
    }));

    const trip = prevDestinationsDetails[index].destinations[0];
    const tripValue = trip ? `${ trip.startDate }-${ trip.endDate }` : null;

    if (tripValue) {
      const newState = !checkedDestinations[destinationName];

      if (!newState) {
        delete selectedTrips[destinationName];
      } else {
        setSelectedTrips((prev) => ({
          ...prev,
          [destinationName]: tripValue
        }));
      }
    }
  };

  const onValidate = async () => {
    // Trigger validation for all fields
    const isValid = await trigger();

    if (isValid) {
      console.log('Validation successful');
      // Handle successful validation logic here
    } else {
      console.log('Validation failed');
      // Handle validation failure logic here
    }
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isValid = await onValidate();
      return isValid;
    }
  }));

  const addDestination = () => {
    append({
      city: '', country: '', startDate: null, endDate: null, transportationMethodTo: '', transportationToInformation: '', transportationMethodWithin: '', transportationWithinInformation: '', stayInformation: ''
    });
  };

  const removeDestination = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const moveDestination = (fromIndex, toIndex) => {
    move(fromIndex, toIndex);
  };

  const fetchTravelRestrictions = async (country, startDate, endDate, index) => {
    if (country && startDate && endDate) {
      const formattedStartDate = startDate.format('MM/DD/YYYY');
      const formattedEndDate = endDate.format('MM/DD/YYYY');
      const response = await fetch(`/referenceData/isTravelRestricted?name=${ country }&arriveDate=${ formattedStartDate }&departDate=${ formattedEndDate }`);
      const data = await response.json();
      if (data.isTravelRestricted) {
        setWarnings((prev) => {
          const newWarnings = [...prev];
          newWarnings[index] = (
            <>
              {data.warningMessage}&nbsp;
              {data.advisoryLink && (<Link href={data.advisoryLink} target='_blank'>Click here for more information.</Link>)}
            </>
          )
          return newWarnings;
        });
      } else {
        setWarnings((prev) => {
          const newWarnings = [...prev];
          newWarnings[index] = null;
          return newWarnings;
        });
      }
    }
  };
  return (
    <Container>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form>
          {fields.map((item, index) => (
            <Card key={item.id} variant='outlined' style={{ marginBottom: '1rem' }}>
              <CardContent>
                <Grid container direction='row' alignItems='center' justifyContent='space-between'>
                  <Grid item xs={10} container alignItems='center'>
                    {fields.length > 1 && (
                      <Grid item>
                        <IconButton onClick={() => removeDestination(index)} color='error'>
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    )}
                    <Grid item>
                      <Typography variant='h5'>
                        Destination #{index + 1}: {getDestinationName(destinations[index])}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={2} container direction='column' justifyContent='center' alignItems='flex-end'>
                    <Grid item>
                      <IconButton onClick={() => moveDestination(index, index - 1)} disabled={index === 0} color='primary' size='medium'>
                        <ArrowUpwardIcon />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton onClick={() => moveDestination(index, index + 1)} disabled={index === fields.length - 1}>
                        <ArrowDownwardIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
                {warnings[index] && (
                  <Alert severity='warning' sx={{ marginBottom: 2 }}>{warnings[index]}</Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormTextField
                      control={control}
                      formErrors={errors}
                      label='City '
                      formFieldId={`foreignTravel.destinations[${ index }].city`}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormAutocomplete
                      control={control}
                      formErrors={errors}
                      label='Country '
                      formFieldId={`foreignTravel.destinations[${ index }].country`}
                      required
                      onAdditionalMethod={(value) => {
                        fetchTravelRestrictions(value, getValues(`foreignTravel.destinations[${ index }].startDate`), getValues(`foreignTravel.destinations[${ index }].endDate`), index);
                      }}
                      freeSolo={false}
                      options={countryOptions}
                      fetchedOptions={(searchText) => fetchCountryOptions(searchText, setCountryOptions)}
                      noOptionsText='Type to search for Countries'
                      disableClearable={false}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormDatePicker
                      control={control}
                      formErrors={errors}
                      label='Start Date '
                      formFieldId={`foreignTravel.destinations[${ index }].startDate`}
                      required
                      validationOptions={{
                        validate: (value) => {
                          const endDate = getValues(`foreignTravel.destinations[${ index }].endDate`);
                          return (
                            !endDate || new Date(value) <= new Date(endDate) || 'Start Date must be before End Date'
                          );
                        }
                      }}
                      views={['year', 'month', 'day']}
                      onAdditionalMethod={(date) => {
                        fetchTravelRestrictions(getValues(`foreignTravel.destinations[${ index }].country`), date, getValues(`foreignTravel.destinations[${ index }].endDate`), index);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormDatePicker
                      control={control}
                      formErrors={errors}
                      label='End Date '
                      formFieldId={`foreignTravel.destinations[${ index }].endDate`}
                      required
                      validationOptions={{
                        validate: (value) => {
                          const startDate = getValues(`foreignTravel.destinations[${ index }].startDate`);
                          return (
                            !startDate || new Date(value) >= new Date(startDate) || 'End Date must be after Start Date'
                          );
                        }
                      }}
                      views={['year', 'month', 'day']}
                      onAdditionalMethod={(date) => {
                        fetchTravelRestrictions(getValues(`foreignTravel.destinations[${ index }].country`), getValues(`foreignTravel.destinations[${ index }].startDate`), date, index);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormDropDown
                      control={control}
                      formErrors={errors}
                      label='Type of Transportation To Destination '
                      formFieldId={`foreignTravel.destinations[${ index }].transportationMethodTo`}
                      required
                      options={initialData.data}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      control={control}
                      formErrors={errors}
                      label='Transportation To Destination Information '
                      formFieldId={`foreignTravel.destinations[${ index }].transportationToInformation`}
                      required
                      multiline
                      maxRows={4}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormDropDown
                      control={control}
                      formErrors={errors}
                      label='Type of Transportation Within Destination '
                      formFieldId={`foreignTravel.destinations[${ index }].transportationMethodWithin`}
                      required
                      options={initialData.data}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      control={control}
                      formErrors={errors}
                      label='Transportation Within Destination information '
                      formFieldId={`foreignTravel.destinations[${ index }].transportationWithinInformation`}
                      required
                      multiline
                      maxRows={4}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      control={control}
                      formErrors={errors}
                      label='Stay Information '
                      formFieldId={`foreignTravel.destinations[${ index }].stayInformation`}
                      required
                      multiline
                      maxRows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <ButtonGroup
            variant='contained'
          >
            <Button
              onClick={addDestination}
              variant='contained'
              color='success'
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              Add Destination
            </Button>
            <Button
              size='small'
              color='success'
              onClick={handleToggleMenu}
            >
              <ArrowDropDownIcon />
            </Button>
            <Menu
              id='split-button-menu'
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              sx={{ marginTop: '10px' }}
            >
              <MenuItem onClick={handleMenuClick}>
                Add Previous Destination
              </MenuItem>
            </Menu>
          </ButtonGroup>

          <Dialog open={modalOpen} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Add Previous Destination</DialogTitle>
            <DialogContent dividers>
              <List>
                { !previousDestinations || previousDestinations.length === 0 ? (
                  <Typography variant='h6'>
                    There is no previous destinations.
                  </Typography>
                ) : previousDestinations?.map((destination, index) => {
                  const city = destination.city && typeof destination.city === 'string' ? destination.city : '';
                  const country = destination.country && typeof destination.country === 'string' ? destination.country : '';
                  const location = [city, country].filter(Boolean).join(', ');
                  const numVisits = prevDestinationsDetails?.[index]?.destinations?.length ?? 0;

                  return (
                    <div key={location}>
                      <ListItem>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={checkedDestinations[destination.city + destination.country] || false}
                              onChange={() => handleDestinationCheck(destination.city + destination.country, index)}
                            />
                          )}
                          label={`${ location } (${ numVisits } Trip${ numVisits > 1 ? 's' : '' })`}
                        />
                        <Button onClick={() => handleDestinationClick(destination.city + destination.country)}>
                          {openDestination[destination.city + destination.country] ? <ExpandLess /> : <ExpandMore />}
                        </Button>
                      </ListItem>
                      <Collapse in={openDestination[destination.city + destination.country]} timeout='auto' unmountOnExit>
                        <RadioGroup
                          value={selectedTrips[destination.city + destination.country] || ''}
                          onChange={(event) => handleTripSelect(destination.city + destination.country, event.target.value)}
                          sx={{ paddingLeft: '32px' }}
                        >
                          {prevDestinationsDetails && prevDestinationsDetails.length > 0 && prevDestinationsDetails[index].destinations.map((trip) => {
                            const { startDate, endDate } = trip;
                            const key = [city, country, startDate, endDate].filter(Boolean).join('-');

                            return (
                              <FormControlLabel
                                key={key}
                                value={`${ startDate }-${ endDate }`}
                                control={<Radio />}
                                label={`Trip: ${ startDate } - ${ endDate }`}
                                disabled={!checkedDestinations[destination.city + destination.country]}
                                sx={{ marginLeft: '16px' }}
                              />
                            )
                          })}
                        </RadioGroup>
                      </Collapse>
                    </div>
                  );
                })}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color='primary'>
                Cancel
              </Button>
              <Button onClick={handleAddPreviousDestinations} color='primary'>
                Add
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      </LocalizationProvider>
    </Container>
  );
});

DestinationsForm.propTypes = {
  initialData: PropTypes.object.isRequired
};

export default DestinationsForm;
